/*
 * Graded Cards adapter for js-theme-singles-grid.js
 * ASSET: assets/js-theme-graded-grid.js.liquid
 *
 * Shared engine: loading, sorting, filtering, grid/list state, drawer,
 * wishlist/cart reinitialization. This file owns slab-specific cards and
 * front/back previews. It never renders condition/quantity/notify controls.
 */
(function (window, document) {
  "use strict";

  var rootSelector = '[data-singles-collection][data-game="graded"]';
  var root = document.querySelector(rootSelector);

  var instance = null;
  // Desktop floating photo popup is paused for now. Set true to restore it.
  // The zoom cursor, Front + Back cue, and click/tap modal stay enabled.
  var ENABLE_DESKTOP_HOVER_GALLERY = false;
  var previewTrigger = null;
  var lastModalTrigger = null;
  var gallery = document.querySelector('[data-graded-hover-gallery]');
  var modal = document.querySelector('[data-graded-modal]');
  var escapeHtml = null;
  var escapeAttribute = null;

  function text(value) {
    return String(value == null ? "" : value).trim();
  }
  function lower(value) {
    return text(value).toLowerCase();
  }
  function firstNonempty() {
    for (var i = 0; i < arguments.length; i += 1) {
      if (text(arguments[i])) return text(arguments[i]);
    }
    return "";
  }
  function toTags(value) {
    if (Array.isArray(value)) return value.map(text).filter(Boolean);
    return text(value) ? text(value).split(/,\s*/).map(text).filter(Boolean) : [];
  }
  function exactTag(tags, values) {
    var accepted = values.map(lower);
    return tags.find(function (tag) { return accepted.indexOf(lower(tag)) !== -1; }) || "";
  }
  function taggedValue(tags, prefixes) {
    for (var i = 0; i < prefixes.length; i += 1) {
      var prefix = lower(prefixes[i]);
      for (var j = 0; j < tags.length; j += 1) {
        var candidate = text(tags[j]);
        if (lower(candidate).indexOf(prefix) === 0) {
          return candidate.slice(prefix.length).trim();
        }
      }
    }
    return "";
  }
  function gameFromVendor(vendor, tags) {
    /* Vendor is the authoritative game source when one-to-one. */
    var value = lower(vendor);
    if (value === "the pokemon company" || value === "the pokémon company") return "Pokémon";
    if (value === "wizards of the coast") return "Magic: The Gathering";
    if (value === "ravensburger") return "Lorcana";

    /* Bandai and other publishers may make multiple games. Use game tags. */
    var gameTag = taggedValue(tags, ["Game-", "Game:"]);
    var aliases = [
      { names: ["pokemon", "pokémon"], label: "Pokémon" },
      { names: ["mtg", "magic", "magic: the gathering", "magic the gathering"], label: "Magic: The Gathering" },
      { names: ["one piece", "onepiece"], label: "One Piece" },
      { names: ["lorcana", "disney lorcana"], label: "Lorcana" },
      { names: ["gundam", "gundam card game"], label: "Gundam" },
      { names: ["riftbound"], label: "Riftbound" },
      { names: ["palworld"], label: "Palworld" },
      { names: ["yu-gi-oh!", "yu-gi-oh", "yugioh"], label: "Yu-Gi-Oh!" },
      { names: ["dragon ball", "dragonball"], label: "Dragon Ball" },
      { names: ["star wars unlimited", "star wars: unlimited"], label: "Star Wars Unlimited" }
    ];
    for (var i = 0; i < aliases.length; i += 1) {
      if (aliases[i].names.indexOf(lower(gameTag)) !== -1) return aliases[i].label;
      if (exactTag(tags, aliases[i].names)) return aliases[i].label;
    }
    return gameTag || "";
  }
  function normalizeCompany(value) {
    var clean = text(value);
    if (/^psa$/i.test(clean)) return "PSA";
    if (/^cgc$/i.test(clean)) return "CGC";
    if (/^bgs$/i.test(clean) || /^beckett(?: grading services)?$/i.test(clean)) return "BGS";
    if (/^sgc$/i.test(clean)) return "SGC";
    return clean;
  }
  function finishFromTags(tags) {
    return exactTag(tags, [
      "Normal", "Nonfoil", "Non-Foil", "Foil", "Holofoil",
      "Reverse Holofoil", "Reverse Holo", "Etched", "Cosmo Holofoil",
      "Galaxy Holofoil", "Textured Holofoil", "Silver Foil"
    ]);
  }
  function rarityFromTags(tags) {
    return exactTag(tags, [
      "Common", "Uncommon", "Rare", "Double Rare", "Ultra Rare",
      "Illustration Rare", "Special Illustration Rare", "Secret Rare",
      "Hyper Rare", "Mega Hyper Rare", "Amazing Rare", "Promo",
      "Mythic", "Mythic Rare", "Special", "Rainbow Rare"
    ]);
  }
  function hydrate(row) {
    var output = Object.assign({}, row || {});
    var tags = toTags(output.tags);
    output.tags = tags;
    output.company = normalizeCompany(firstNonempty(
      output.company, taggedValue(tags, ["Company-", "GradingCompany-"]),
      exactTag(tags, ["PSA", "CGC", "BGS", "SGC", "Beckett"])
    ));
    output.grade = firstNonempty(output.grade, taggedValue(tags, ["Grade-"]));
    output.grade_year = taggedValue(tags, ["GradeYear-", "GradingYear-"]);
    output.grading_error = taggedValue(tags, ["GradingError-", "GradingError:"]);
    output.set_name = firstNonempty(output.set_name, taggedValue(tags, ["SetName-", "SetName:", "Set-"]));
    output.finish = firstNonempty(output.finish, finishFromTags(tags));
    output.rarity = firstNonempty(output.rarity, rarityFromTags(tags));
    output.language = firstNonempty(
      taggedValue(tags, ["Language-", "Language:"]),
      exactTag(tags, ["English", "Japanese", "Japan", "JP", "JPN", "French", "German", "Italian", "Spanish", "Korean", "Chinese", "Portuguese"])
    );
    if (["japanese", "japan", "jp", "jpn"].indexOf(lower(output.language)) !== -1) {
      output.language = "Japanese";
    }
    // Japanese is a language distinction, not a separate card game.
    // The same game label (Pokémon) is used across all languages.
    output.game = gameFromVendor(output.vendor, tags);
    output.cert_number = firstNonempty(output.sku, output.barcode);
    output.img_url = text(output.img_url);
    output.img_back_url = text(output.img_back_url);
    output.number = text(output.number);
    output.product_url = firstNonempty(output.product_url, "/products/" + encodeURIComponent(text(output.handle)));
    return output;
  }

  function unique(values) {
    var seen = {};
    return values.filter(function (value) {
      value = text(value);
      var key = lower(value);
      if (!key || seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }
  function singleFacet(field) {
    return function (card) {
      return unique((card.variants || []).map(function (row) { return row[field]; }));
    };
  }
  // Hide a generic finish token from title extras only when it repeats the
  // actual finish metafield. Specific treatments (e.g. Surge Foil with Foil)
  // are intentionally preserved. Shopify's saved product title is unchanged.
  function comparableFinish(value) {
    var compact = lower(value).replace(/[^a-z0-9]+/g, "");
    var aliases = {
      holo: "holofoil",
      reverseholo: "reverseholofoil",
      nonfoil: "normal"
    };
    return aliases[compact] || compact;
  }
  function uniqueTitleExtras(details, finish) {
    var finishKey = comparableFinish(finish);
    return text(details).split(/\s*[,·]\s*/)
      .map(text)
      .filter(Boolean)
      .filter(function (value) {
        return !finishKey || comparableFinish(value) !== finishKey;
      })
      .join(" · ");
  }
  function titleParts(row) {
    var title = text(row.title);
    var details = "";
    var matched = title.match(/^(.*?)\s*\(([^()]*)\)\s*$/);
    if (matched) {
      title = text(matched[1]);
      details = uniqueTitleExtras(matched[2], row.finish);
    }
    var number = text(row.number);
    if (number) {
      var normalized = number.split("/").map(function (part) {
        return part.replace(/^0+(?=\d)/, "");
      }).join("/");
      var candidates = [number, normalized];
      candidates.forEach(function (candidate) {
        if (!candidate) return;
        var safe = candidate.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        title = title.replace(new RegExp("\\s+" + safe + "$", "i"), "").trim();
      });
      number = normalized;
    } else {
      /* Only infer an unambiguous collector-number suffix. */
      var inferred = title.match(/\s+(\d+\/\d+)$/);
      if (inferred) {
        number = inferred[1];
        title = title.slice(0, -inferred[0].length).trim();
      }
    }
    return { name: title || text(row.title), number: number, details: details };
  }
  /* Compact slab-LABEL-inspired wordmarks; these are CSS approximations,
     not copies of the grading companies' copyrighted brand assets. PSA and
     CGC use slab-label styling even when optional logo PNGs are uploaded.
     SGC and Beckett/BGS still support optional PNG logos. */
  function renderCompanyWordmark(company) {
    if (company === "PSA") {
      return '<span class="graded-company-wordmark graded-brand-psa" aria-hidden="true">' +
        '<span>P</span><span>S</span><span>A</span></span>';
    }
    if (company === "CGC") {
      return '<span class="graded-company-wordmark graded-brand-cgc" aria-hidden="true">' +
        '<b>CGC</b></span>';
    }
    if (company === "SGC") {
      return '<span class="graded-company-wordmark graded-brand-sgc" aria-hidden="true">SGC</span>';
    }
    if (company === "BGS") {
      return '<span class="graded-company-wordmark graded-brand-bgs" aria-hidden="true">' +
        '<b>B</b><small>BECKETT</small></span>';
    }
    return '<span class="graded-company-wordmark graded-brand-generic" aria-hidden="true">' +
      escapeHtml(company || "Unspecified") + '</span>';
  }
  function renderGradeBar(row) {
    var company = normalizeCompany(row.company) || "Unspecified";
    var urls = window.GradedCompanyLogoUrls || {};
    var logoUrl = text(urls[company]);
    var image = logoUrl
      ? '<img class="graded-company-logo" src="' + escapeAttribute(logoUrl) +
        '" alt="" decoding="async">'
      : '';
    var companyClass = lower(company).replace(/[^a-z0-9]+/g, "-");
    var gradeText = text(row.grade) || "Grade unavailable";
    var pristine = gradeText.match(/^Pristine\s+(10(?:\.\d+)?)$/i);
    var gradeMarkup = pristine
      ? '<span class="graded-grade-modifier">Pristine</span> ' +
        '<span class="graded-grade-number">' + escapeHtml(pristine[1]) + '</span>'
      : escapeHtml(gradeText);
    return '<div class="graded-grade-bar" aria-label="Grading company and grade">' +
      '<span class="graded-grade-company graded-company--' +
        escapeAttribute(companyClass) + '" role="img" aria-label="' +
        escapeAttribute(company) + '">' + image + renderCompanyWordmark(company) + '</span>' +
      '<span class="graded-grade-value' + (pristine ? " graded-grade-value--pristine" : "") + '">' + gradeMarkup + '</span>' +
      '</div>';
  }
  function bindCompanyLogos() {
    function update(event, loaded) {
      var img = event.target;
      if (!img || !img.matches || !img.matches(".graded-company-logo")) return;
      var panel = img.closest(".graded-grade-company");
      if (panel) panel.classList.toggle("has-loaded-logo", loaded && img.naturalWidth > 0);
    }
    /* Capturing load/error handles logo images inserted by dynamic grid rendering. */
    root.addEventListener("load", function (event) { update(event, true); }, true);
    root.addEventListener("error", function (event) { update(event, false); }, true);
  }
  function renderIdentity(row, parts) {
    // Game and set stay together; language moves to quieter metadata.
    // Finish remains visible only once, even with title suffixes.
    var bits = [parts.number, row.rarity, row.finish, row.language];
    var clean = unique(bits);
    var details = [parts.details].concat(clean).filter(Boolean);
    var out = '<div class="graded-identity">';
    out += '<div class="graded-game-set">' +
      '<div class="graded-game-row">' +
      '<span class="graded-game-pill">' + escapeHtml(row.game || "Game not specified") + '</span>';
    if (row.set_name) out += '<span class="graded-set-name">' + escapeHtml(row.set_name) + '</span>';
    out += '</div></div>';
    if (details.length) out += '<div class="graded-card-details">' +
      details.map(escapeHtml).join(' <span aria-hidden="true">·</span> ') + '</div>';
    if (row.grading_error) out += '<div class="graded-error-note">Grading Error: ' +
      escapeHtml(row.grading_error) + '</div>';
    out += '</div>';
    return out;
  }

  // Match the grid layout: certification and grading year sit directly
  // underneath the company/grade bar, with no duplicates above it.
  function renderMinorLine(row) {
    var cert = text(row.cert_number);
    var year = text(row.grade_year);
    if (!cert && !year) return "";
    return '<div class="graded-minor-line">' +
      (cert ? '<span>Cert #' + escapeHtml(cert) + '</span>' : '') +
      (year ? '<span>Graded ' + escapeHtml(year) + '</span>' : '') +
      '</div>';
  }

  function renderPhoto(row, mode, title, side) {
    var front = text(row.img_url);
    var back = text(row.img_back_url);
    var cssClass = mode === "list" ? "singles-list-image" : "singles-grid-image";
    var imageToShow = side === "back" ? back : side === "front" ? front : (front || back);
    var imageSide = side === "back" ? "Back" : (side === "front" || front ? "Front" : "Back");
    var sideClass = side ? " graded-card-photo--" + side : "";
    var hasThisPhoto = Boolean(imageToShow);
    var ariaLabel = hasThisPhoto
      ? 'View available slab photos of ' + title
      : (side ? imageSide + ' photo coming soon for ' + title : 'Slab photos coming soon for ' + title);
    var out = '<button type="button" class="' + cssClass + ' graded-card-photo' + sideClass + '"' +
      ' data-graded-preview-trigger' +
      ' data-front-url="' + escapeAttribute(front) + '"' +
      ' data-back-url="' + escapeAttribute(back) + '"' +
      ' data-title="' + escapeAttribute(title) + '"' +
      ' data-product-url="' + escapeAttribute(row.product_url) + '"' +
      ' aria-label="' + escapeAttribute(ariaLabel) + '"' +
      (!hasThisPhoto ? ' disabled' : '') + '>';
    if (hasThisPhoto) {
      out += '<img class="graded-card-thumb" src="' + escapeAttribute(imageToShow) + '" alt="' +
        imageSide + ' of ' + escapeAttribute(title) + '" loading="lazy" decoding="async">';
    } else {
      var missingLabel = side ? imageSide + ' photo coming soon' : 'Slab photos coming soon';
      out += '<span class="graded-image-missing">' + escapeHtml(missingLabel) + '</span>';
    }
    if (hasThisPhoto) {
      // Grid keeps Front + Back cue; desktop list labels its visible sides.
      var cue = side ? imageSide : (front && back ? 'Front + Back' : 'View Slab Photo');
      // With only the front thumbnail visible on narrow screens, signal
      // that its click gallery still contains both sides.
      if (side === "front" && front && back) {
        cue = 'Front<span class="graded-mobile-back-hint"> + Back</span>';
      }
      out += '<span class="graded-photo-cue" aria-hidden="true">' +
        '<svg class="graded-zoom-symbol" viewBox="0 0 24 24" focusable="false">' +
        '<circle cx="10.7" cy="10.7" r="6.7"></circle>' +
        '<path d="M15.6 15.6L21 21"></path></svg>' + cue + '</span>';
    }
    out += '</button>';
    return out;
  }

  // Desktop list always reserves front and back slots. Missing photos
  // display dedicated placeholders instead of shrinking the image column.
  // On narrow screens we show one slot, preferring the back when it is the
  // only photo available; the full gallery stays accessible for real photos.
  function renderListPhotos(row, title) {
    var onlyBack = !text(row.img_url) && Boolean(text(row.img_back_url));
    return '<div class="graded-list-photos has-back' + (onlyBack ? ' only-back' : '') + '">' +
      renderPhoto(row, "list", title, "front") +
      renderPhoto(row, "list", title, "back") + '</div>';
  }

  function renderWishlist(row) {
    var id = text(row.product_id || row.id);
    return '<a href="' + escapeAttribute(row.product_url) + '"' +
      ' class="singles-grid-heart t4s-pr-item-btn t4s-pr-wishlist graded-wishlist"' +
      ' data-action-wishlist data-singles-wishlist data-id="' + escapeAttribute(id) + '"' +
      ' data-product-id="' + escapeAttribute(id) + '" rel="nofollow" aria-label="Add to wishlist">' +
      '<span class="t4s-svg-pr-icon"><svg viewBox="0 0 24 24" aria-hidden="true">' +
      '<use xlink:href="#t4s-icon-wis" href="#t4s-icon-wis"></use></svg></span></a>';
  }
  function renderCart(row) {
    if (!row.variant_id) return '<span class="graded-no-variant">Purchase unavailable</span>';
    return '<form class="singles-kalles-cart-form t4s-form__product graded-cart-form"' +
      ' action="/cart/add" method="post" enctype="multipart/form-data"' +
      ' data-type="add-to-cart-form" data-singles-cart-form>' +
      '<input type="hidden" name="id" value="' + escapeAttribute(row.variant_id) + '">' +
      '<input type="hidden" name="quantity" value="1">' +
      '<div class="t4s-product-form__buttons" style="--pr-btn-round:999px">' +
      '<div class="graded-cart-controls t4s-d-flex t4s-flex-wrap">' +
      '<button type="submit" name="add" data-atc-form' +
      ' class="t4s-product-form__submit t4s-btn t4s-btn-base t4s-btn-style-default ' +
      't4s-btn-color-primary t4s-w-100 t4s-justify-content-center t4s-btn-loading__svg singles-add-to-cart">' +
      '<span class="t4s-btn-atc_text">Add to Cart</span>' +
      '<span class="t4s-loading__spinner" hidden>' +
      '<svg width="16" height="16" hidden class="t4s-svg-spinner" focusable="false" role="presentation" viewBox="0 0 66 66">' +
      '<circle class="t4s-path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>' +
      '</svg></span></button>' + renderWishlist(row) + '</div></div></form>';
  }
  function renderPurchase(row, mode) {
    var stocked = row.available === true && Number(row.quantity) > 0;
    var result = '<div class="graded-purchase ' + (stocked ? '' : 'is-sold-out') + '">';
    if (stocked) {
      result += '<strong class="graded-price">' + escapeHtml(text(row.price_display) || "Price unavailable") + '</strong>';
      result += renderCart(row);
    } else {
      result += '<strong class="graded-sold-out" role="status">OUT OF STOCK</strong>';
      result += '<span class="graded-sold-out-detail">This graded slab is unavailable</span>';
    }
    return result + '</div>';
  }
  function renderGridCard(card, row) {
    var parts = titleParts(row);
    var title = parts.name;
    var longPrice = row.available === true && Number(row.quantity) > 0 && text(row.price_display).length >= 14;
    return '<article class="singles-grid-card graded-grid-card' +
      (longPrice ? ' graded-grid-card--long-price' : '') + '" data-singles-card data-singles-grid-card' +
      ' data-card-key="' + escapeAttribute(card.key) + '" data-product-handle="' + escapeAttribute(card.key) + '"' +
      ' data-has-stock="' + (row.available ? 'true' : 'false') + '">' +
      renderPhoto(row, "grid", title) +
      '<div class="singles-grid-body graded-grid-body">' +
      '<div class="singles-grid-title-row graded-title-row">' +
      '<a href="' + escapeAttribute(row.product_url) + '" class="singles-grid-title graded-grid-title">' +
      escapeHtml(title) + '</a>' +
      '<div class="singles-grid-title-extra graded-title-extra">' +
      escapeHtml([parts.number, parts.details].filter(Boolean).join(" · ")) + '</div>' +
      '<div class="singles-grid-title-divider"></div></div>' +
      renderIdentity(row, Object.assign({}, parts, { number: "", details: "" })) +
      renderGradeBar(row) +
      renderMinorLine(row) +
      renderPurchase(row, "grid") + '</div></article>';
  }
  // List-only buy panel: price is outside and ABOVE the bordered panel.
  // Each graded product has one purchasable slab, so there is no quantity
  // selector, condition tab, or back-in-stock button.
  function renderListPurchase(row) {
    var stocked = row.available === true && Number(row.quantity) > 0;
    if (!stocked) {
      return '<div class="graded-list-purchase-panel is-sold-out">' +
        '<strong class="graded-sold-out" role="status">OUT OF STOCK</strong>' +
        '</div>';
    }
    var count = Math.max(0, Math.floor(Number(row.quantity) || 0));
    return '<div class="graded-list-purchase-panel">' +
      '<div class="graded-list-stock" aria-label="' + escapeAttribute(count + ' in stock') + '">' +
      '<strong class="graded-list-stock-count">' + count + '</strong>' +
      '<span class="graded-list-stock-label">in stock</span></div>' +
      renderCart(row) + '</div>';
  }

  // V8: keep the certificate unobtrusive in the list card's upper-right.
  // It no longer extends the grade rail below the purchase panel.
  // Grade year, when known, shares this small metadata line.
  function renderListCornerMetadata(row) {
    var cert = text(row.cert_number);
    var year = text(row.grade_year);
    if (!cert && !year) return "";
    return '<div class="graded-list-corner-meta" aria-label="Slab identification">' +
      (year ? '<span class="graded-list-corner-year">Graded ' + escapeHtml(year) + '</span>' : '') +
      (cert ? '<span class="graded-list-corner-cert">Cert #' + escapeHtml(cert) + '</span>' : '') +
      '</div>';
  }

  function renderListCard(card, row) {
    var parts = titleParts(row);
    // Collector number is a TITLE EXTRA, not part of the small metadata line.
    var extras = [parts.number, parts.details].filter(Boolean).join(" · ");
    var stocked = row.available === true && Number(row.quantity) > 0;
    // Reserve a little extra space for unusually long currency strings.
    var longPrice = stocked && text(row.price_display).length >= 14;
    return '<article class="singles-list-card graded-list-card graded-list-card--two-photos' +
      (longPrice ? ' graded-list-card--long-price' : '') + '"' +
      ' data-singles-card data-singles-list-card' +
      ' data-card-key="' + escapeAttribute(card.key) + '" data-product-handle="' + escapeAttribute(card.key) + '"' +
      ' data-has-stock="' + (row.available ? 'true' : 'false') + '">' +
      renderListCornerMetadata(row) +
      renderListPhotos(row, parts.name) +
      '<div class="graded-list-info">' +
      '<a class="graded-list-title" href="' + escapeAttribute(row.product_url) + '">' + escapeHtml(parts.name) + '</a>' +
      (extras ? '<div class="graded-list-title-extras">' + escapeHtml(extras) + '</div>' : '') +
      '<div class="graded-list-title-divider" aria-hidden="true"></div>' +
      renderIdentity(row, {number: "", details: ""}) + '</div>' +
      // A single framed right-hand unit joins the grading rail to the purchase box.
      '<div class="graded-list-actions">' +
      '<div class="graded-list-price-row"' + (!stocked ? ' data-graded-unavailable="true"' : '') + '>' +
      (stocked
        ? '<strong class="graded-price">' + escapeHtml(text(row.price_display) || "Price unavailable") + '</strong>'
        : '<span class="graded-list-unavailable-note">This graded slab is unavailable</span>') +
      '</div>' +
      '<div class="graded-list-action-bottom">' +
      '<div class="graded-list-grading">' +
      renderGradeBar(row) + '</div>' +
      renderListPurchase(row) +
      '</div></div></article>';
  }

  function getPreviewData(button) {
    return {
      front: text(button.getAttribute("data-front-url")),
      back: text(button.getAttribute("data-back-url")),
      title: text(button.getAttribute("data-title")),
      url: text(button.getAttribute("data-product-url"))
    };
  }
  function setFigure(figure, imageUrl, alt) {
    if (!figure) return;
    var image = figure.querySelector("img");
    if (!imageUrl) {
      figure.hidden = true;
      if (image) image.removeAttribute("src");
      return;
    }
    figure.hidden = false;
    if (image) { image.src = imageUrl; image.alt = alt; }
  }
  function placeGallery(button) {
    if (!gallery || gallery.hidden) return;
    var rect = button.getBoundingClientRect();
    var width = gallery.getBoundingClientRect().width || 530;
    var height = gallery.getBoundingClientRect().height || 420;
    var sideGap = 12;
    var left = rect.right + sideGap;
    if (left + width > window.innerWidth - 10) left = rect.left - width - sideGap;
    if (left < 10) left = Math.max(10, (window.innerWidth - width) / 2);
    var top = Math.max(10, Math.min(rect.top, window.innerHeight - height - 10));
    gallery.style.left = Math.round(left) + "px";
    gallery.style.top = Math.round(top) + "px";
  }
  function openHover(button) {
    if (!ENABLE_DESKTOP_HOVER_GALLERY || !gallery ||
      !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    var data = getPreviewData(button);
    if (!data.front && !data.back) return;
    previewTrigger = button;
    setFigure(gallery.querySelector("[data-graded-hover-front]"), data.front, "Front of " + data.title);
    setFigure(gallery.querySelector("[data-graded-hover-back]"), data.back, "Back of " + data.title);
    gallery.hidden = false;
    placeGallery(button);
  }
  function closeHover() {
    previewTrigger = null;
    if (gallery) gallery.hidden = true;
  }
  function openModal(button) {
    if (!modal) return;
    var data = getPreviewData(button);
    if (!data.front && !data.back) return;
    closeHover();
    lastModalTrigger = button;
    setFigure(modal.querySelector("[data-graded-modal-front]"), data.front, "Front of " + data.title);
    setFigure(modal.querySelector("[data-graded-modal-back]"), data.back, "Back of " + data.title);
    var heading = modal.querySelector("[data-graded-modal-title]");
    if (heading) heading.textContent = data.title + " — Slab Photos";
    var link = modal.querySelector("[data-graded-modal-link]");
    if (link) link.href = data.url || "#";
    modal.hidden = false;
    document.body.classList.add("graded-modal-open");
    var close = modal.querySelector("[data-graded-modal-close]");
    if (close) close.focus();
  }
  function closeModal() {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.body.classList.remove("graded-modal-open");
    ["[data-graded-modal-front]", "[data-graded-modal-back]"].forEach(function (selector) {
      var img = modal.querySelector(selector + " img");
      if (img) img.removeAttribute("src");
    });
    if (lastModalTrigger && document.contains(lastModalTrigger)) lastModalTrigger.focus();
    lastModalTrigger = null;
  }
  function bindPhotos() {
    // Paused desktop hover-popup listeners. Re-enable with the flag at top;
    // click/tap always opens the front/back modal at every screen size.
    if (ENABLE_DESKTOP_HOVER_GALLERY) {
      root.addEventListener("pointerover", function (event) {
        var button = event.target.closest("[data-graded-preview-trigger]");
        if (!button || !root.contains(button) || button.contains(event.relatedTarget)) return;
        openHover(button);
      });
      root.addEventListener("pointerout", function (event) {
        var button = event.target.closest("[data-graded-preview-trigger]");
        if (!button || !root.contains(button) || button.contains(event.relatedTarget)) return;
        if (previewTrigger === button) closeHover();
      });
      root.addEventListener("focusin", function (event) {
        var button = event.target.closest("[data-graded-preview-trigger]");
        if (button) openHover(button);
      });
      root.addEventListener("focusout", function (event) {
        var button = event.target.closest("[data-graded-preview-trigger]");
        if (button && previewTrigger === button) closeHover();
      });
    }
    root.addEventListener("click", function (event) {
      var button = event.target.closest("[data-graded-preview-trigger]");
      if (!button || !root.contains(button)) return;
      event.preventDefault();
      openModal(button);
    });
    if (modal) {
      modal.addEventListener("click", function (event) {
        if (event.target === modal || event.target.closest("[data-graded-modal-close]")) closeModal();
      });
      modal.addEventListener("keydown", function (event) {
        if (event.key !== "Tab") return;
        var focusable = Array.prototype.slice.call(modal.querySelectorAll('button:not([disabled]), a[href]'));
        if (!focusable.length) return;
        var first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      });
    }
    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape") { closeHover(); closeModal(); }
    });
    window.addEventListener("scroll", function () {
      if (previewTrigger) placeGallery(previewTrigger);
    }, { passive: true });
    window.addEventListener("resize", closeHover);
    document.addEventListener("singles:grid:rendered", function (event) {
      if (event.detail && event.detail.game === "graded") closeHover();
    });
  }

  function initialize() {
    if (instance || !window.SinglesGrid || !window.SinglesGrid.version) return;
    var api = window.SinglesGrid;
    escapeHtml = api.helpers.escapeHtml;
    escapeAttribute = api.helpers.escapeAttribute;

    var facets = [
      { key: "game", label: "Card Game" },
      { key: "language", label: "Language" },
      { key: "company", label: "Grading Company", order: ["PSA", "CGC", "BGS", "SGC"] },
      { key: "grade", label: "Grade", order: ["Pristine 10", "10", "9.5", "9", "8.5", "8", "7.5", "7", "6", "5", "Authentic"] },
      { key: "grading_error", label: "Grading Error" },
      { key: "set_name", label: "Set", control: "search-multiselect", placeholder: "Search sets...", maxSuggestions: 50 },
      { key: "finish", label: "Finish", order: ["Normal", "Holofoil", "Reverse Holofoil"] },
      { key: "rarity", label: "Rarity", control: "search-multiselect", placeholder: "Search rarity..." }
    ];
    facets.forEach(function (facet) { facet.getValues = singleFacet(facet.key); });

    var nativeSorts = [
      "manual", "best-selling", "title-ascending",
      "price-ascending", "price-descending",
      "created-descending", "created-ascending"
    ];

    instance = api.create({
      id: "graded-collection",
      game: "graded",
      storageNamespace: "graded",
      root: rootSelector,
      grid: "[data-singles-grid]",
      loading: "[data-singles-grid-loading]",
      jsonUrl: root.getAttribute("data-json-url"),
      productCount: Number(root.getAttribute("data-product-count") || 0),
      pageSize: 50,
      /* Adapter-managed Shopify sorting prevents the shared client-side
         sorter from incorrectly falling back to A-Z for date sorts. */
      autoLoad: false,
      defaultAvailability: "instock",
      persistAvailability: false,
      clearAvailability: "all",
      defaultView: "grid",
      defaultSort: nativeSorts.indexOf(root.getAttribute("data-default-sort")) !== -1
        ? root.getAttribute("data-default-sort") : "manual",
      allowedSorts: nativeSorts,
      syncSortToUrl: true,
      emptyMessage: "No graded cards match the current filters.",
      errorHeading: "The graded card collection could not be loaded.",
      filterDefinitions: facets,
      renderer: { renderGridCard: renderGridCard, renderListCard: renderListCard }
    });

    /* ================================================================
       Graded-only dependent facets (no shared SinglesGrid modifications).

       For each facet, ignore that facet's own selections and respect every
       OTHER selected facet, current name query, and availability state.
       This is standard faceted navigation: options with no possible cards
       become grey/disabled, but always remain on-screen. Selected values
       are NEVER disabled, so they can always be removed.
       ================================================================ */
    function facetValueKeys(card, key) {
      return unique((card.variants || []).map(function (variant) {
        return text(variant[key]);
      })).map(lower);
    }

    function matchesOtherFilters(card, exceptKey) {
      if (instance.state.availability === "instock" &&
          !(card.variants || []).some(function (row) {
            return row.available === true || row.available === "true" || row.available === 1;
          })) return false;
      if (!instance.matchesName(card)) return false;

      return facets.every(function (facet) {
        if (facet.key === exceptKey) return true;
        var selected = instance.state.filters[facet.key] || [];
        if (!selected.length) return true;
        var values = facetValueKeys(card, facet.key);
        return selected.some(function (value) { return values.indexOf(lower(value)) !== -1; });
      });
    }

    function refreshDependentFacets() {
      if (!instance || !instance.state || !instance.state.cards.length) return;
      facets.forEach(function (facet) {
        var counts = Object.create(null);
        instance.state.cards.forEach(function (card) {
          if (!matchesOtherFilters(card, facet.key)) return;
          facetValueKeys(card, facet.key).forEach(function (value) {
            counts[value] = (counts[value] || 0) + 1;
          });
        });

        var container = root.querySelector('[data-singles-filter-options="' + facet.key + '"]');
        if (!container) return;
        var selected = (instance.state.filters[facet.key] || []).map(lower);
        container.querySelectorAll('[data-singles-filter-value]').forEach(function (button) {
          // Search control's Remove chips must always remain operable.
          if (button.hasAttribute('data-singles-search-remove')) return;
          var value = lower(button.getAttribute('data-singles-filter-value'));
          var active = selected.indexOf(value) !== -1;
          var unavailable = !active && !(counts[value] > 0);
          button.disabled = unavailable;
          button.classList.toggle('graded-filter-unavailable', unavailable);
          if (unavailable) {
            button.setAttribute('aria-disabled', 'true');
            button.setAttribute('title', 'No matches with current filters');
          } else {
            button.removeAttribute('aria-disabled');
            button.removeAttribute('title');
          }
        });
      });
    }

    // The shared engine recreates facet HTML when filters change; update
    // disabled states AFTER rendering, including name and stock changes.
    var sharedRender = instance.render.bind(instance);
    instance.render = function () {
      var result = sharedRender();
      refreshDependentFacets();
      return result;
    };

    // Available choices sort ahead of dimmed choices in searchable dropdowns,
    // so default 8 suggestions do not crowd out valid sets/rarities.
    var sharedUpdateSuggestions = instance.updateSearchMultiselectSuggestions.bind(instance);
    instance.updateSearchMultiselectSuggestions = function (input) {
      var control = this.getSearchMultiselectControl(input);
      if (control) {
        var suggestions = control.querySelector('[data-singles-search-suggestions]');
        if (suggestions) {
          Array.prototype.slice.call(suggestions.querySelectorAll('[data-singles-search-option]'))
            .sort(function (a, b) { return Number(a.disabled) - Number(b.disabled); })
            .forEach(function (option) {
              var empty = suggestions.querySelector('[data-singles-search-empty]');
              suggestions.insertBefore(option, empty || null);
            });
        }
      }
      return sharedUpdateSuggestions(input);
    };

    // Keyboard arrows skip disabled choices; mouse and touch still visibly
    // display them in a greyed-out state.
    instance.setSearchMultiselectActiveOption = function (input, nextIndex) {
      var control = this.getSearchMultiselectControl(input);
      if (!control) return;
      var suggestions = control.querySelector('[data-singles-search-suggestions]');
      if (!suggestions) return;
      var enabled = Array.prototype.slice.call(suggestions.querySelectorAll('[data-singles-search-option]'))
        .filter(function (option) { return !option.hidden && !option.disabled; });
      suggestions.querySelectorAll('[data-singles-search-option]').forEach(function (option) {
        option.classList.remove('is-active');
      });
      if (!enabled.length) {
        control._singlesActiveOptionIndex = -1;
        input.removeAttribute('aria-activedescendant');
        return;
      }
      var index = (nextIndex + enabled.length) % enabled.length;
      var active = enabled[index];
      if (!active.id) active.id = suggestions.id + '-available-' + index;
      active.classList.add('is-active');
      control._singlesActiveOptionIndex = index;
      input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    };

    // Defensive event guard for delegated shared filter listeners.
    root.addEventListener('click', function (event) {
      var button = event.target.closest('[data-singles-filter-value].graded-filter-unavailable');
      if (button && root.contains(button)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    /* Shopify owns the source order for each ?sort_by=... value. The shared
       engine handles filtered/title/price sorts; for dates, preserve the
       exact paginated order returned by Shopify, rather than falling back A-Z. */
    var sharedSortCards = instance.sortCards.bind(instance);
    instance.sortCards = function (cards) {
      if (this.state.sort === "created-ascending" || this.state.sort === "created-descending") {
        return cards.slice().sort(function (a, b) { return a.sourceIndex - b.sourceIndex; });
      }
      return sharedSortCards(cards);
    };

    /* Serializes native Shopify requests so a slower old sort response can
       never replace the user's most recent selection. Data for each sort is
       cached independently by the shared collection loader. */
    var desiredServerSort = instance.state.sort;
    var appliedServerSort = "";
    var activeServerSort = "";
    var activeLoad = null;
    var forceNextLoad = false;

    /* Hydrate rows and silently discard a stale response after sort changes. */
    var originalSetRows = instance.setRows.bind(instance);
    instance.setRows = function (rows) {
      if (activeServerSort && desiredServerSort !== activeServerSort) return;
      return originalSetRows((Array.isArray(rows) ? rows : []).map(hydrate));
    };

    function loadShopifyOrder() {
      if (activeLoad) return activeLoad;
      if (appliedServerSort === desiredServerSort && !forceNextLoad) return Promise.resolve();
      var sort = desiredServerSort;
      var force = forceNextLoad;
      forceNextLoad = false;
      activeServerSort = sort;
      activeLoad = instance.load({ serverSort: sort, forceReload: force })
        .then(function () {
          appliedServerSort = sort;
          console.log("Graded collection loaded:", instance.state.cards.length, "slabs; Shopify sort:", sort);
        })
        .catch(function (error) {
          appliedServerSort = sort; /* avoid retry loops if Shopify is unavailable */
          console.error("Unable to load graded cards sorted by " + sort + ":", error);
        })
        .finally(function () {
          activeLoad = null;
          activeServerSort = "";
          if (desiredServerSort !== appliedServerSort || forceNextLoad) loadShopifyOrder();
        });
      return activeLoad;
    }

    /* The shared engine updates URL, sort selector, and stored preference.
       Each change then requests Shopify's native collection order in-place,
       preserving the current left-sidebar filter selections and grid/list view. */
    var sharedSetSort = instance.setSort.bind(instance);
    instance.setSort = function (sort) {
      var previous = this.state.sort;
      sharedSetSort(sort);
      desiredServerSort = this.state.sort;
      if (desiredServerSort !== previous) loadShopifyOrder();
    };

    instance.getCardTitle = function (card) {
      var selected = this.getSelectedVariant(card) || (card.variants || [])[0] || {};
      return titleParts(selected).name;
    };

    bindPhotos();
    bindCompanyLogos();
    window.GradedGrid = {
      getInstance: function () { return instance; },
      reload: function () {
        desiredServerSort = instance.state.sort;
        forceNextLoad = true;
        return loadShopifyOrder();
      }
    };
    instance.initialize().then(function () {
      desiredServerSort = instance.state.sort;
      return loadShopifyOrder();
    }).catch(function (error) {
      console.error("Graded collection could not start:", error);
    });
  }

    // Expose the exact collection card designs to search pages.
  window.GradedCardRenderer = {
    hydrate: hydrate,

    renderGridCard: function (card, row, api) {
      escapeHtml = api.helpers.escapeHtml;
      escapeAttribute = api.helpers.escapeAttribute;
      return renderGridCard(card, hydrate(row));
    },

    renderListCard: function (card, row, api) {
      escapeHtml = api.helpers.escapeHtml;
      escapeAttribute = api.helpers.escapeAttribute;
      return renderListCard(card, hydrate(row));
    },

    bindSearchPage: function (searchRoot) {
      if (!searchRoot || root) return;

      root = searchRoot;
      gallery = document.querySelector('[data-graded-hover-gallery]');
      modal = document.querySelector('[data-graded-modal]');

      bindPhotos();
      bindCompanyLogos();
    }
  };

  // Only start the collection loader on collection pages.
  if (!root || window.GradedGrid) return;

  if (window.SinglesGrid && window.SinglesGrid.version) initialize();
  else {
    document.addEventListener("singles:grid:ready", initialize, { once: true });
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", initialize, { once: true });
    } else {
      window.setTimeout(initialize, 0);
    }
  }
})(window, document);
